/**
 * WHERE A PERFORMANCE CHIP MAY BE CARRIED — and the exceptions EZRA prints.
 *
 * There are THREE rules in the corpus about chips and the BOTTOM group, and no
 * two of them say the same thing. All three are quoted verbatim below because
 * the whole of this module is the difference between them.
 *
 * The base rulebook, p.xv:
 *
 *   "Finally, NOTE that performance chips cannot be carried by drivers in the
 *    BOTTOM group. If a driver with a performance chip is sent to the BOTTOM
 *    group and cannot use the chip to prevent it--for example, if he has a
 *    PROBLEM--he must forfiet the chip."
 *
 * EZRA-S2 §4.0, Driver Trait Notes:
 *
 *   "Performance Chips awarded by a driver trait [FN] or [ST] cannot be used in
 *    the Bottom Group [BG] or in the Lap Down Group [LDG]. The Performance Chip
 *    is not lost. If the driver gets back to the Middle Group [MG] or the Top
 *    Group [TG] the Performance Chip will be available to use."
 *
 * EZRA-S2 §3.12, BGC Performance Chips:
 *
 *   "Performance Chips will be allowed, but these have no affect outside the BG.
 *    They function only within the BG and then only during a BGC."
 *   "BGC Performance Chips are designated by a token of a different color from
 *    that used for PC's in the Middle and Top Groups that arise from standard
 *    RWBR play procedures."
 *   "BG PC's cannot be used in connection with a feud/fume involving a higher
 *    group car during a Regular Turn."
 *   "Cars in BG with PCs that move to Middle Group during a Regular Turn lose
 *    their BGC PC(s) when exiting the BG."
 *
 * So: an ORDINARY chip is FORFEITED on the way down. A TRAIT chip is only
 * SUSPENDED — held, unusable, and handed back the moment its owner climbs out.
 * A **BG chip is the mirror image of the ordinary one**: it exists only where an
 * ordinary chip cannot be carried, and it is destroyed on the way UP.
 *
 * D-EZ-1 settles which governs where they overlap (EZRA), and each EZRA note is
 * explicitly scoped — one to chips "awarded by a driver trait", one to chips
 * that "arise from" a Churn — so all three coexist rather than one replacing
 * another.
 *
 * THE REPRESENTATION, and why it is shaped this way.
 *
 * Every consumer in the engine reads `Car.chips` and means "chips this driver
 * can spend right now" — trouble checks, quality challenges, duels, the pit
 * shuffle, the photo finish. Rather than teach all of them a second rule, the
 * suspension is modelled as a MOVE:
 *
 *   `chips`      — usable right now. Unchanged meaning; nothing else had to
 *                  learn anything.
 *   `traitChips` — how many of `chips` came from a trait, so a demotion knows
 *                  how many to protect. Never exceeds `chips`.
 *   `heldChips`  — trait chips parked off to one side because their owner is
 *                  somewhere he cannot use them. Unspendable by construction:
 *                  no code path can spend a chip that is not in `chips`.
 *   `bgChips`    — the token of a different colour. Also not in `chips`, for
 *                  exactly the same reason, and see below.
 *
 * Spending therefore comes out of the ORDINARY pile first, and that is a
 * ruling, not an accident — see `traitChipsOf`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-39 — THE BG PERFORMANCE CHIP, AND WHY IT CANNOT LEAK
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This closes D-EZ-34, which recorded the BG chip as awarded-but-unspendable.
 *
 * §3.12 gives the BG chip a scope no other chip has: **only in the BOTTOM group,
 * and only during a Churn.** A rule with two conditions is a rule two callers
 * will eventually forget one of, so it is not enforced by asking callers to
 * remember. It is enforced three separate ways, each of which is sufficient on
 * its own:
 *
 *   1. IT IS NOT IN `chips`, SO NOTHING CAN SPEND IT. Every chip-spending path
 *      in this engine — `challenge.ts`, `duel.ts`, `trouble.ts`, `pitTurn.ts`,
 *      `photoFinish.ts` — reads `Car.chips` (or a `Working` map fed from it).
 *      A count that never enters `chips` is unspendable by construction, which
 *      is the same argument that makes `heldChips` safe, and it needs no new
 *      code anywhere else in the engine.
 *
 *   2. IT CANNOT COME INTO EXISTENCE OUTSIDE THE BOTTOM GROUP. `awardBgChip`
 *      refuses anywhere outside it and says why. There is no other way to raise
 *      the count. "Outside it" is `chipsAreBlocked`, not a bare position test —
 *      see the ⚠ note on `bgChipsSpendable` for the bug that distinction fixed.
 *
 *   3. IT CANNOT SURVIVE OUTSIDE THE BOTTOM GROUP. `restoreChips` — reached
 *      from `scopeChips`, which `renumber` runs over every card at the end of
 *      every turn, and which every placement helper in `lapsDown.ts` and
 *      `pitTurn.ts` already calls — destroys it. That is not an extra check
 *      bolted on; it is the printed sentence *"Cars in BG with PCs that move to
 *      Middle Group during a Regular Turn lose their BGC PC(s) when exiting the
 *      BG"* filed in the one place the engine already turns a position into a
 *      chip consequence.
 *
 * The second condition — *"and then only during a BGC"* — is the one a type
 * cannot fully police, and this says so rather than pretending otherwise. What
 * it does instead is make the only read that yields a spendable number demand
 * the phase in writing: `bgChipsSpendable(car, { position, phase })` returns 0
 * unless `phase` is the literal `'bottom-group-churn'`. A future caller in the
 * feud code cannot reach the count by accident — it would have to name the
 * Churn to get it, which is a statement somebody can find and argue with rather
 * than an omission nobody can see. `bgChipsOf` is the raw count and is for
 * display, the ledger and the award/spend helpers; it is never a licence.
 *
 * That construction also settles two printed sentences for free:
 *
 *   *"BG PC's cannot be used in connection with a feud/fume involving a higher
 *    group car during a Regular Turn."* — a feud is not a Churn, so the phase
 *   gate refuses it. Nothing had to be written into `feudFume.ts`.
 *
 *   *"these have no affect outside the BG"* — the position gate refuses it, and
 *   in any case (3) means the count is already zero by the time its owner is
 *   anywhere else.
 *
 * THE CAP. §3.1 — *"Drivers may have a maximum of two (2) Performance Chips
 * available on any single game turn"* — is in the general Performance Chip
 * Rules and §3.12 prints no cap of its own, so the general rule governs
 * (D-EZ-1). It also lands cleanly: an ordinary chip cannot be carried in the
 * BOTTOM group at all (p.xv) and a trait chip is suspended there (§4.0), so
 * while a car is in the BOTTOM group its BG chips ARE every chip it has
 * "available", and capping `bgChips` at two satisfies §3.1 exactly.
 *
 * The excess is **not** sent to reserve. "Extra Chips in Excess of Two" is
 * written about "pc's" and its three clauses — activate one at the start of a
 * turn, lose them all on losing track position, none carries to the next race —
 * describe the ordinary pile; extending them to a different-coloured token
 * would be inventing a rule rather than applying one. A refused award is
 * counted and surfaced (`npm run surge` prints it) so the cost of that reading
 * is a number rather than an assumption.
 */

import { groupOf, type Car } from './types.ts';
import type { ChipDelta } from './ruleEvent.ts';

export const CITE_CHIP_BAN =
  'RB/xv — "performance chips cannot be carried by drivers in the BOTTOM group. If a driver with a ' +
  'performance chip is sent to the BOTTOM group and cannot use the chip to prevent it... he must forfiet the chip."';

export const CITE_RESERVE =
  'EZRA-S2 "Extra Chips in Excess of Two" — "If a driver gains a PC while already holding 2 pc\'s, ' +
  'the extra pc (or pc\'s) are held in \u201creserve\u201d. When all active PC\'s are expended, he must ' +
  'activate a Reserve PC at the beginning of the next turn (prior to any actions in that turn). ' +
  'Only one reserve pc can be activated in a turn. Reserve pc\'s are retained if he 1) retains his ' +
  'track position or 2) improves his track position. If he loses track position for any reason, ' +
  'reserve pcs are lost. Reserve pc\'s have no value until activated. Reserve PC\'s do not carry ' +
  'over to the next race."';

/** "If a driver gains a PC while already holding 2 pc's..." */
export const MAX_ACTIVE_CHIPS = 2;

export const CITE_TRAIT_CHIPS =
  'EZRA-S2 §4.0 Driver Trait Notes — "Performance Chips awarded by a driver trait [FN] or [ST] cannot be ' +
  'used in the Bottom Group [BG] or in the Lap Down Group [LDG]. The Performance Chip is not lost. If the ' +
  'driver gets back to the Middle Group [MG] or the Top Group [TG] the Performance Chip will be available to use."';

/** A trait chip suspended or handed back, for the ledger. */
export interface ChipHold {
  driverId: string;
  /** How many trait chips moved. */
  chips: number;
  /** true = handed back on returning to MG/TG; false = parked on the way down. */
  restored: boolean;
  /** Set when this was a chip pushed into RESERVE by the cap of two (D-EZ-28). */
  reserve?: boolean;
}

/**
 * Trait chips among a car's spendable pile.
 *
 * RULING (D-EZ-26, part 1): a spend comes out of the ORDINARY chips first.
 * Neither sheet says which pile a spend empties, and it only matters because
 * the two piles have different fates — an ordinary chip is destroyed by a trip
 * to the BOTTOM group and a trait chip survives it. Spending the perishable one
 * first is what a person holding two differently-coloured chips would do, and
 * it is the only reading under which the EZRA sentence "the Performance Chip is
 * not lost" survives contact with a driver who spends a chip and is demoted in
 * the same turn. It falls out for free here: `traitChips` is only ever reduced
 * by the clamp below, i.e. once `chips` has dropped beneath it.
 */
export function traitChipsOf(car: Car): number {
  return Math.min(Math.max(0, car.traitChips ?? 0), Math.max(0, car.chips));
}

/** Chips in excess of two. No value until activated (EZRA-S2). */
export function reserveChipsOf(car: Car): number {
  return Math.max(0, car.reserveChips ?? 0);
}

/**
 * Take a chip, honouring the cap of two active.
 *
 * ⚠ THE COLLISION WITH D-EZ-26, AND HOW IT IS SETTLED.
 *
 * EZRA says two different things about losing a chip, in two different
 * sections, and a driver can be in both situations at once:
 *
 *   §4.0        a chip a TRAIT awarded is "not lost" — suspended in the BOTTOM
 *               group, handed back on the way out.
 *   Excess of 2 a chip in RESERVE is lost "if he loses track position for any
 *               reason".
 *
 * A trait chip sitting in reserve when its owner drops a place is covered by
 * both, and they disagree. Neither sheet anticipates the other.
 *
 * RULED (D-EZ-28): **ordinary chips go to reserve before trait chips do.** The
 * trait chip stays ACTIVE and the ordinary one is displaced, so the situation
 * where the two rules contradict each other cannot arise. This is not a dodge —
 * it is the reading that makes both printed sentences true at once, which is
 * the same test D-EZ-13 was settled on.
 *
 * It also falls out almost for free: EZRA allows one trait per driver, FN and
 * ST each pay once per race, so a driver can hold at most ONE trait chip in a
 * race. Reserve is therefore ordinary chips by construction, and a test asserts
 * it rather than leaving it to be true by accident.
 */
export function gainChip(car: Car, count = 1, fromTrait = false): { car: Car; toReserve: number } {
  const active = Math.max(0, car.chips);
  const room = Math.max(0, MAX_ACTIVE_CHIPS - active);

  if (fromTrait) {
    // A trait chip always takes an active seat if there is one, and if there is
    // not, it displaces an ORDINARY chip into reserve rather than going there
    // itself. `traitChipsOf` clamps, so the count cannot exceed what is held.
    const ordinaryActive = active - traitChipsOf(car);
    if (room >= count) {
      return {
        car: { ...car, chips: active + count, traitChips: traitChipsOf(car) + count },
        toReserve: 0,
      };
    }
    const displaced = Math.min(count - room, ordinaryActive);
    return {
      car: {
        ...car,
        chips: Math.min(MAX_ACTIVE_CHIPS, active + count),
        traitChips: traitChipsOf(car) + count,
        reserveChips: reserveChipsOf(car) + displaced,
      },
      toReserve: displaced,
    };
  }

  const taken = Math.min(room, count);
  const spare = count - taken;
  if (spare === 0) return { car: { ...car, chips: active + taken }, toReserve: 0 };
  return {
    car: { ...car, chips: active + taken, reserveChips: reserveChipsOf(car) + spare },
    toReserve: spare,
  };
}

/**
 * "When all active PC's are expended, he must activate a Reserve PC at the
 * beginning of the next turn (prior to any actions in that turn). Only one
 * reserve pc can be activated in a turn."
 *
 * Note "must" — it is not a choice, which is what lets a solitaire engine run
 * it without asking anybody.
 */
export function activateReserve(car: Car): { car: Car; activated: boolean } {
  if (reserveChipsOf(car) === 0) return { car, activated: false };
  if (Math.max(0, car.chips) > 0) return { car, activated: false };
  const left = reserveChipsOf(car) - 1;
  return {
    car: { ...car, chips: 1, reserveChips: left === 0 ? undefined : left },
    activated: true,
  };
}

/**
 * "If he loses track position for any reason, reserve pcs are lost."
 *
 * `was` is where he was; `now` is where he is. A bigger number is further back.
 * Retaining OR improving keeps them, which the sheet spells out as two numbered
 * cases rather than leaving to inference.
 */
export function reserveAfterMove(car: Car, was: number, now: number): { car: Car; lost: number } {
  const held = reserveChipsOf(car);
  if (held === 0 || now <= was) return { car, lost: 0 };
  return { car: { ...car, reserveChips: undefined }, lost: held };
}

/** Trait chips parked because their owner cannot use them where he is. */
export function heldChipsOf(car: Car): number {
  return Math.max(0, car.heldChips ?? 0);
}

// ---------------------------------------------------------------------------
// THE BG PERFORMANCE CHIP — EZRA-S2 §3.12 (D-EZ-39)
// ---------------------------------------------------------------------------

export const CITE_BG_CHIP =
  'EZRA-S2 §3.12 BGC Performance Chips — "Performance Chips will be allowed, but these have no ' +
  'affect outside the BG. They function only within the BG and then only during a BGC." + "BGC ' +
  'Performance Chips are designated by a token of a different color from that used for PC\'s in the ' +
  'Middle and Top Groups that arise from standard RWBR play procedures."';

export const CITE_BG_CHIP_LOST =
  'EZRA-S2 §3.12 — "Cars in BG with PCs that move to Middle Group during a Regular Turn lose their ' +
  'BGC PC(s) when exiting the BG."';

export const CITE_BG_CHIP_NO_FEUD =
  'EZRA-S2 §3.12 — "BG PC\'s cannot be used in connection with a feud/fume involving a higher group ' +
  'car during a Regular Turn."';

export const CITE_BG_CHIP_CAP =
  'EZRA-S2 §3.1 Performance Chip Rules — "Drivers may have a maximum of two (2) Performance Chips ' +
  'available on any single game turn." §3.12 prints no cap of its own, so the general rule governs ' +
  '(D-EZ-1); and in the BOTTOM group a car\'s BG chips are every chip it has available, because an ' +
  'ordinary chip cannot be carried there (RB/xv) and a trait chip is suspended there (§4.0).';

/**
 * The cap on BG chips.
 *
 * Deliberately expressed as `MAX_ACTIVE_CHIPS` rather than a fresh `2`: it is
 * the same printed maximum, and if §3.1's number ever turns out to be something
 * else, one edit moves both.
 */
export const MAX_BG_CHIPS = MAX_ACTIVE_CHIPS;

/**
 * The raw BG chip count.
 *
 * For DISPLAY, the ledger, and the award/spend helpers below. **Not a licence
 * to spend** — `bgChipsSpendable` is the only read that answers "may this be
 * spent right now", and it is the one every contest must use.
 */
export function bgChipsOf(car: Car): number {
  return Math.max(0, car.bgChips ?? 0);
}

/**
 * The only phase in which a BG chip is worth anything.
 *
 * A string literal rather than a boolean so that the call site has to NAME the
 * Churn to get a non-zero answer. A boolean `inChurn: true` passed from the
 * feud code would look like a typo; `phase: 'bottom-group-churn'` passed from
 * the feud code is a claim somebody can find and refute.
 */
export type ChipPhase = 'bottom-group-churn' | 'regular-turn';

/**
 * How many BG chips this car may spend, at this position, in this phase.
 *
 * *"They function only within the BG and then only during a BGC."* Both halves,
 * in one place, returning a number every caller can use exactly the way it uses
 * `Car.chips` — which is the point. See the D-EZ-39 note at the head of this
 * file for why this is a gate rather than a convention, and for the three
 * independent barriers that stand behind it.
 *
 * ⚠ "IN THE BG" IS `chipsAreBlocked`, NOT `groupOf(position) === 'bottom'`, AND
 * THE DIFFERENCE WAS A BUG THIS FILE SHIPPED FOR ABOUT AN HOUR.
 *
 * The first version tested the position alone. `npm run surge` then reported 117
 * cars "outside the BOTTOM group holding a BG chip" over 2,000 races, every one
 * of them a LAPPED car sitting at P18 or P19 — a field worn down to 27 cars puts
 * the laps-down sub-group above the boards' fixed P20 boundary, because the
 * boundary is drawn for a 43-car field and the group ranges never shrink.
 *
 * Such a car has not *"exited the BG"*: §3.13 defines *"LDSG = Lap Down Sub-Group:
 * BG cars designated as a lap (laps) down and trailing the LLSG"*, and §3.12 makes
 * the whole BG eligible for a Churn *"including the Lap Down Sub-Group (LDSG)"*.
 * A lapped car is in the BOTTOM group by definition, whatever number the boards
 * happen to print beside him.
 *
 * `chipsAreBlocked(position, lapsDown)` is exactly that set, and it is already
 * the engine's answer to "where is an ordinary chip no good" — so using it here
 * makes the two printed rules **exact complements**:
 *
 *   RB/xv        an ORDINARY chip is no good in the BOTTOM group (or a lap down).
 *   EZRA §3.12   a BG chip is no good ANYWHERE ELSE.
 *
 * One predicate, two rules, and no way for the pair to drift out of agreement.
 * That is a stronger construction than two position tests that happen to line
 * up, and the counting is what turned up the difference.
 */
export function bgChipsSpendable(car: Car, at: { position: number; phase: ChipPhase }): number {
  if (at.phase !== 'bottom-group-churn') return 0;
  if (!chipsAreBlocked(at.position, car.lapsDown)) return 0;
  return bgChipsOf(car);
}

/** Why an award of a BG chip was refused, or null when it was not. */
export type BgChipRefusal = 'outside-the-bottom-group' | 'at-the-cap-of-two';

/**
 * Hand a car the BG performance chip §3.12 awards on the Porch, or §3.13 awards
 * to a car that surges while already on the BG lead position.
 *
 * Refuses outright outside the BOTTOM group — barrier (2) of D-EZ-39. There is
 * no other way to raise the count, so a BG chip cannot come into existence
 * anywhere it could not be spent.
 */
export function awardBgChip(
  car: Car,
  position: number,
  count = 1,
): { car: Car; awarded: number; refused: BgChipRefusal | null } {
  // The same predicate `bgChipsSpendable` uses, for the same reason — see the ⚠
  // note there. A chip may not be created anywhere it could not be spent, and
  // "the BG" includes the laps-down sub-group wherever attrition has left it.
  if (!chipsAreBlocked(position, car.lapsDown)) {
    return { car, awarded: 0, refused: 'outside-the-bottom-group' };
  }
  const held = bgChipsOf(car);
  const room = Math.max(0, MAX_BG_CHIPS - held);
  const awarded = Math.min(room, count);
  if (awarded === 0) return { car, awarded: 0, refused: 'at-the-cap-of-two' };
  return {
    car: { ...car, bgChips: held + awarded },
    awarded,
    refused: awarded < count ? 'at-the-cap-of-two' : null,
  };
}

/** Spend BG chips. Clamped at zero; the caller has already checked the gate. */
export function spendBgChips(car: Car, count = 1): { car: Car; spent: number } {
  const held = bgChipsOf(car);
  const spent = Math.min(held, Math.max(0, count));
  if (spent === 0) return { car, spent: 0 };
  const left = held - spent;
  return { car: { ...car, bgChips: left === 0 ? undefined : left }, spent };
}

/**
 * Every PERFORMANCE CHIP a driver holds, spendable or not. For display and for
 * the audit.
 *
 * **BG chips are deliberately NOT counted here**, and that is the printed
 * distinction rather than an oversight: they are *"a token of a different color
 * from that used for PC's in the Middle and Top Groups"*. Folding them in would
 * make the dossier show a bottom-group car holding a performance chip it cannot
 * spend and will lose the moment it climbs out, and would make a retirement's
 * forfeit line count two different currencies as one. `bgChipsOf` is the
 * separate count, reported separately everywhere.
 */
export function totalChipsOf(car: Car): number {
  return Math.max(0, car.chips) + heldChipsOf(car) + reserveChipsOf(car);
}

/** Is this a place where no chip may be spent? BOTTOM group, or a lap down. */
export function chipsAreBlocked(position: number, lapsDown: boolean): boolean {
  return groupOf(position) === 'bottom' || lapsDown;
}

/** Normalise `traitChips` down to what the car actually holds. */
function clamped(car: Car): Car {
  const trait = traitChipsOf(car);
  if ((car.traitChips ?? 0) === trait) return car;
  return trait === 0 ? { ...car, traitChips: undefined } : { ...car, traitChips: trait };
}

export interface ChipMove {
  car: Car;
  /** Ordinary chips destroyed (RB/xv). */
  forfeited: number;
  /** Trait chips suspended (EZRA-S2 §4.0). */
  parked: number;
  /** Trait chips handed back (EZRA-S2 §4.0). */
  restored: number;
  /** BG chips destroyed by leaving the BOTTOM group (EZRA-S2 §3.12, D-EZ-39). */
  bgLost: number;
}

const STILL: Omit<ChipMove, 'car'> = { forfeited: 0, parked: 0, restored: 0, bgLost: 0 };

/**
 * A car is going into the BOTTOM group or a lap down. Ordinary chips are gone;
 * trait chips are parked.
 *
 * Call this at the MOMENT of the move rather than leaving it to `renumber`, so
 * the ledger reads truthfully — the same reason `sendLapDown` has always
 * stripped chips itself.
 */
export function parkChips(car: Car): ChipMove {
  const trait = traitChipsOf(car);
  const forfeited = Math.max(0, car.chips) - trait;
  // A BG chip is UNTOUCHED here, and that is the whole asymmetry of D-EZ-39: the
  // BOTTOM group is the one place it is worth something, so arriving there
  // cannot cost it. Nothing needs to be written — `bgChips` is simply carried
  // through by the spread — but it is worth saying out loud, because the reader
  // arriving from p.xv will expect a chip to die on this line.
  if (forfeited === 0 && trait === 0) return { car: clamped(car), ...STILL };
  const held = heldChipsOf(car) + trait;
  return {
    car: { ...car, chips: 0, traitChips: undefined, heldChips: held === 0 ? undefined : held },
    forfeited,
    parked: trait,
    restored: 0,
    bgLost: 0,
  };
}

/**
 * A car is back in the MIDDLE or TOP group. Parked trait chips return to hand —
 * and any BG chip is destroyed.
 *
 * *"Cars in BG with PCs that move to Middle Group during a Regular Turn lose
 * their BGC PC(s) when exiting the BG."*
 *
 * This is barrier (3) of D-EZ-39, and it is here rather than at each of the
 * dozen places a card can be moved for the same reason the p.xv forfeit is:
 * `scopeChips` is reached by `renumber` (every card, end of every turn) and by
 * every placement helper in `lapsDown.ts` and `pitTurn.ts` that already has to
 * settle chips at a destination. A rule enforced by the representation is a rule
 * no future caller can forget.
 *
 * Note which branch this is: `scopeChips` sends a car here only when
 * `chipsAreBlocked` is false — MIDDLE or TOP group and not a lap down — which is
 * exactly "exiting the BG". A lapped car sitting in the BOTTOM group goes to
 * `parkChips` and keeps its BG chip, which is right: it has not exited anything,
 * and §3.12 makes the whole BG eligible for a Churn, *"including the Lap Down
 * Sub-Group (LDSG)"*.
 */
export function restoreChips(car: Car): ChipMove {
  const held = heldChipsOf(car);
  const bgLost = bgChipsOf(car);
  if (held === 0 && bgLost === 0) return { car: clamped(car), ...STILL };
  const base = bgLost === 0 ? car : { ...car, bgChips: undefined };
  if (held === 0) return { car: clamped(base), forfeited: 0, parked: 0, restored: 0, bgLost };
  return {
    car: {
      ...base,
      chips: Math.max(0, car.chips) + held,
      traitChips: traitChipsOf(car) + held,
      heldChips: undefined,
    },
    forfeited: 0,
    parked: 0,
    restored: held,
    bgLost,
  };
}

/**
 * A car is out of the race. Everything is void — including parked chips, since
 * "if the driver gets back to the Middle Group" is a condition he can no longer
 * satisfy, and including any BG chip, for which the same is true twice over.
 *
 * `forfeited` counts PERFORMANCE CHIPS only and `bgVoided` is reported apart,
 * because the ledger line built from `forfeited` is denominated in performance
 * chips and a token of a different colour is not one. See `totalChipsOf`.
 */
export function voidChips(car: Car): { car: Car; forfeited: number; bgVoided: number } {
  const total = totalChipsOf(car);
  const bgVoided = bgChipsOf(car);
  if (total === 0 && bgVoided === 0) return { car: clamped(car), forfeited: 0, bgVoided: 0 };
  return {
    car: {
      ...car,
      chips: 0,
      traitChips: undefined,
      heldChips: undefined,
      reserveChips: undefined,
      bgChips: undefined,
    },
    forfeited: total,
    bgVoided,
  };
}

/** Park or restore, according to where the car now sits. The whole rule, once. */
export function scopeChips(car: Car, position: number): ChipMove {
  return chipsAreBlocked(position, car.lapsDown) ? parkChips(car) : restoreChips(car);
}

/**
 * Hand a driver a chip his TRAIT earned him (FN, ST).
 *
 * If he is somewhere he cannot use it, it goes straight into the held pile — it
 * is awarded, it is not lost, and it is not usable. All three at once, which is
 * exactly what §4.0 describes.
 */
export function awardTraitChip(car: Car, position: number, count = 1): { car: Car; usable: boolean } {
  if (chipsAreBlocked(position, car.lapsDown)) {
    return { car: { ...car, heldChips: heldChipsOf(car) + count }, usable: false };
  }
  return {
    car: { ...car, chips: Math.max(0, car.chips) + count, traitChips: traitChipsOf(car) + count },
    usable: true,
  };
}

/** The ledger line for an ordinary forfeit. */
export function forfeitDelta(driverId: string, chips: number): ChipDelta {
  return { driverId, delta: -chips, reason: 'performance chips cannot be carried in the BOTTOM group (p.xv)' };
}

/** One sentence describing a batch of holds and restores, or null if there were none. */
export function describeHolds(holds: readonly ChipHold[], nameOf: (id: string) => string): string | null {
  if (holds.length === 0) return null;
  const parts = holds.map((h) =>
    h.restored
      ? `${nameOf(h.driverId)} is back where he can use it and takes his ${h.chips === 1 ? 'trait chip' : `${h.chips} trait chips`} back`
      : `${nameOf(h.driverId)} keeps ${h.chips === 1 ? 'his trait chip' : `his ${h.chips} trait chips`} but cannot spend ${h.chips === 1 ? 'it' : 'them'} down here`,
  );
  return `${parts.join('; ')}.`;
}
